import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Container,
  Divider,
  Drawer,
  FileInput,
  Group,
  Menu,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconArrowsRight,
  IconBox,
  IconBrandGoogleDrive,
  IconCheck,
  IconClipboardText,
  IconCode,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconFileImport,
  IconFileSpreadsheet,
  IconLink,
  IconCopy,
  IconPlus,
  IconPrinter,
  IconQrcode,
  IconSearch,
  IconTable,
  IconTrash,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type {
  BulkAssignResult,
  Customer,
  DeliveryType,
  Device,
  Shipment,
  ShipmentStatus,
} from "../api/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLocalAgent } from "../hooks/useLocalAgent";
import { localAgentPrint, localAgentPrintJobs } from "../api/localAgent";
import { ShipmentDocuments } from "../components/shipments/ShipmentDocuments";
import { useStatusDocsGate } from "../components/shipments/StatusDocsGate";
import { ShipmentStatusBadges } from "../components/shipments/ShipmentStatusBadges";
import { ExportActModal } from "../components/ExportActModal";
import {
  AntennaConfirmModal,
  antennaCandidates,
  antennaSkippedMessage,
  cleanDevEui,
} from "../components/AntennaConfirmModal";
import { ExportToSheetModal } from "../components/shipments/ExportToSheetModal";
import { PrintPassportLabelsModal } from "../components/shipments/PrintPassportLabelsModal";
import { deliveryTypeLabel, shipmentStatusLabel } from "../lib/shipmentLabels";
import { antennaErrorMessage } from "../lib/antennaErrors";
import { formatTypeCounts } from "../lib/typeCounts";

const ALL_STATUSES = [
  "received",
  "scanned",
  "keyed",
  "flashed",
  "tested",
  "labeled",
  "assigned",
  "shipped",
  "delivered",
  "returned",
  "defect",
];

const PAGE_SIZE = 50;

export function ShipmentDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const shipmentId = Number(id);
  const user = useCurrentUser();
  const localAgent = useLocalAgent();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canEdit = user.role === "admin" || user.role === "operator";
  const canDelete = user.role === "admin";

  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ezplPreview, setEzplPreview] = useState<{
    size: "small" | "large";
    code: string;
    bytes: number;
    ezpl: string;
  } | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);
  // One-time "finishing actions" prompt shown when the shipment is opened from
  // the scanner ("Vezi livrarea" → ?from=scan). Stays until an action or close.
  const [showScanActions, setShowScanActions] = useState(
    () => searchParams.get("from") === "scan",
  );
  const [batchChunkSize, setBatchChunkSize] = useState<number>(25);
  const [batchUseModel, setBatchUseModel] = useState(true);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [moveShipmentOpen, setMoveShipmentOpen] = useState(false);
  const [setBoxOpen, setSetBoxOpen] = useState(false);
  const [bulkEditDevicesOpen, setBulkEditDevicesOpen] = useState(false);
  const [markDefectOpen, setMarkDefectOpen] = useState(false);
  const [defectReason, setDefectReason] = useState("");
  const [allShipments, setAllShipments] = useState<Shipment[]>([]);

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage(visible: Device[]) {
    setSelected((prev) => {
      const allSelected = visible.every((d) => prev.has(d.id));
      const next = new Set(prev);
      if (allSelected) for (const d of visible) next.delete(d.id);
      else for (const d of visible) next.add(d.id);
      return next;
    });
  }

  async function applyBulk(updates: Record<string, unknown>, msg: string) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const r = await api.bulkUpdateDevices(ids, updates);
      notifications.show({
        color: r.updated > 0 ? "green" : "yellow",
        message: `${msg} · ${r.updated}/${r.total}`,
      });
      setSelected(new Set());
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.operationFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function deleteSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(t("shipmentDetail.confirmDeleteDevices", { count: ids.length }))) return;
    try {
      const r = await api.bulkDeleteDevices(ids);
      notifications.show({
        color: "green",
        message: t("shipmentDetail.devicesDeletedToast", { count: r.deleted }),
      });
      setSelected(new Set());
      await load();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.deleteDevicesFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function handlePreviewEzpl(size: "small" | "large") {
    try {
      const r = await api.godexPreviewShipmentEzpl(shipmentId, size);
      setEzplPreview({ size, code: r.code, bytes: r.bytes, ezpl: r.ezpl });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.previewFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function handlePrintBatches() {
    const size = Math.max(1, Math.min(100, batchChunkSize || 25));
    setBatchSubmitting(true);
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintShipmentBatchesPayload(shipmentId, {
          chunk_size: size,
          use_model: batchUseModel,
        });
        await localAgentPrintJobs(r.jobs, r.delay_ms, localAgent.selectedId);
        notifications.show({
          color: "green",
          message: t("shipmentDetail.labelsSentDetailed", {
            labels: r.total,
            devices: r.devices ?? r.total * size,
            size,
          }),
        });
      } else {
        const r = await api.godexPrintShipmentBatches(shipmentId, {
          chunk_size: size,
          use_model: batchUseModel,
        });
        notifications.show({
          color: "green",
          message:
            r.labels && r.devices
              ? t("shipmentDetail.labelsSentDetailed", { labels: r.labels, devices: r.devices, size })
              : t("shipmentDetail.labelsSent"),
        });
      }
      setBatchOpen(false);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? JSON.stringify(err.detail).slice(0, 200)
          : err instanceof Error
            ? err.message
            : t("common.error");
      notifications.show({
        color: "red",
        title: t("shipmentDetail.printFailed"),
        message: msg,
      });
    } finally {
      setBatchSubmitting(false);
    }
  }

  async function handlePrintPartida(size: "small" | "large") {
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintShipmentPayload(shipmentId, size);
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintShipment(shipmentId, size);
      }
      notifications.show({
        color: "green",
        message: t("shipmentDetail.partidaLabelSent", {
          size: size === "large" ? "54×40" : "53×30",
        }),
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? JSON.stringify(err.detail).slice(0, 200)
          : err instanceof Error
            ? err.message
            : t("common.error");
      notifications.show({
        color: "red",
        title: t("shipmentDetail.printFailed"),
        message: msg,
      });
    }
  }

  async function handlePrintLink() {
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintLinkPayload(shipmentId, "small");
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintLink(shipmentId, "small");
      }
      notifications.show({ color: "green", message: t("shipmentDetail.linkLabelSent") });
    } catch (err) {
      const msg = err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200)
        : err instanceof Error ? err.message : t("common.error");
      notifications.show({ color: "red", title: t("shipmentDetail.printFailed"), message: msg });
    }
  }

  async function handleCopyPublicLink() {
    try {
      const r = await api.ensurePublicLink(shipmentId);
      await navigator.clipboard.writeText(r.public_url);
      notifications.show({ color: "green", message: t("shipmentDetail.publicLinkCopied") });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      notifications.show({ color: "red", message: msg });
    }
  }

  async function handleRevokePublicLink() {
    try {
      await api.revokePublicLink(shipmentId);
      notifications.show({ color: "green", message: t("shipmentDetail.publicLinkRevoked") });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      notifications.show({ color: "red", message: msg });
    }
  }

  async function handleDelete() {
    if (
      !window.confirm(
        t("shipmentDetail.confirmDeleteShipment", {
          code: shipment?.code,
          count: devices.length,
        }),
      )
    )
      return;
    try {
      const r = await api.deleteShipment(shipmentId);
      notifications.show({
        color: "green",
        message: t("shipmentDetail.shipmentDeletedToast", { count: r.detached_devices }),
      });
      navigate("/shipments");
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.deleteShipmentFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    }
  }

  async function load() {
    setLoading(true);
    try {
      const s = await api.getShipment(shipmentId);
      const [c, devs] = await Promise.all([
        s.customer_id != null
          ? api.getCustomer(s.customer_id).catch(() => null)
          : Promise.resolve(null),
        api.listShipmentDevices(shipmentId),
      ]);
      setShipment(s);
      setCustomer(c);
      setDevices(devs);
    } catch {
      notifications.show({
        color: "red",
        title: t("common.error"),
        message: t("shipmentDetail.loadFailed"),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (Number.isFinite(shipmentId)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  useEffect(() => {
    void api.listShipments().then(setAllShipments).catch(() => undefined);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter(
      (d) =>
        d.dev_eui.toLowerCase().includes(q) ||
        (d.box_number ?? "").toLowerCase().includes(q) ||
        (d.model ?? "").toLowerCase().includes(q) ||
        (d.serial_number ?? "").toLowerCase().includes(q) ||
        (d.qr_payload ?? "").toLowerCase().includes(q),
    );
  }, [devices, search]);

  const scanOrder = useMemo(() => {
    const ranked = devices
      .filter((d) => d.scanned_at)
      .sort((a, b) => (a.scanned_at ?? "").localeCompare(b.scanned_at ?? ""));
    const map = new Map<number, number>();
    ranked.forEach((d, i) => map.set(d.id, i + 1));
    return map;
  }, [devices]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageDevices = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleExport(accounting = false) {
    if (!shipment) return;
    window.location.href = api.exportShipmentUrl(shipment.id, { accounting });
  }

  async function handleUploadDrive() {
    if (!shipment) return;
    try {
      const r = await api.uploadShipmentToDrive(shipment.id);
      notifications.show({
        color: "green",
        title: t("shipmentDetail.uploadOkTitle"),
        message: r.file.name,
      });
      setShipment(r.shipment);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("shipmentDetail.uploadFailed");
      notifications.show({ color: "red", title: "Drive", message: msg });
    }
  }

  async function printDeviceLabel(deviceId: number) {
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintDevicePayload(
          deviceId,
          customer ? shipment?.code : undefined,
        );
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintDevice(deviceId, customer ? shipment?.code : undefined);
      }
      notifications.show({
        color: "green",
        title: t("shipmentDetail.labelSentTitle"),
        message: t("shipmentDetail.labelSentBytes", { bytes: "–" }),
        autoClose: 1500,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.detail === "object" &&
        err.detail !== null &&
        "detail" in err.detail
          ? String((err.detail as Record<string, unknown>).detail)
          : err instanceof Error
            ? err.message
            : t("common.error");
      notifications.show({
        color: "red",
        title: t("shipmentDetail.printDeviceFailed"),
        message: msg,
      });
    }
  }

  async function updateBox(deviceId: number, box: string) {
    try {
      await api.setShipmentBoxes(shipmentId, [{ device_id: deviceId, box_number: box }]);
      setDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? { ...d, box_number: box || null } : d)),
      );
    } catch {
      notifications.show({ color: "red", message: t("shipmentDetail.saveFailed") });
    }
  }

  if (!Number.isFinite(shipmentId)) return <Text c="red">{t("shipmentDetail.invalidId")}</Text>;

  // Defect % is computed over modules only (antennas are not tracked for defects).
  const moduleDevices = devices.filter((d) => (d.product_type_slug ?? "module") === "module");
  const defectCount = moduleDevices.filter((d) => d.status === "defect").length;

  return (
    <Container size="xl">
      <Button
        component={Link}
        to="/shipments"
        variant="light"
        size="sm"
        leftSection={<IconArrowLeft size={16} />}
        mb="sm"
      >
        {t("shipmentDetail.back")}
      </Button>
      <Title order={2} mb="xs">
        {shipment?.code ?? "..."}
      </Title>
      <Group mb="md" wrap="wrap" gap="xs">
        {shipment?.customer_id == null ? (
          <Badge color="gray" variant="light">
            {t("shipmentDetail.internalStockBadge")}
          </Badge>
        ) : (
          <Text c="dimmed" size="sm">
            {t("shipmentDetail.clientLabel", { name: customer?.name ?? t("common.none") })}
          </Text>
        )}
        <Text c="dimmed" size="sm">
          {t("shipmentDetail.locationLabel", { value: shipment?.location ?? t("common.none") })}
        </Text>
        <Text c="dimmed" size="sm">
          {t("shipmentDetail.branchLabel", { value: shipment?.branch ?? t("common.none") })}
        </Text>
        <Text c="dimmed" size="sm">
          {t("shipmentDetail.dateLabel", { value: shipment?.shipped_date ?? t("common.none") })}
        </Text>
        {shipment && <ShipmentStatusBadges shipment={shipment} />}
        <Badge color="blue" variant="light">
          {formatTypeCounts(
            t,
            shipment?.type_counts,
            devices.length,
            t("shipmentDetail.devicesCount", { count: devices.length }),
          )}
        </Badge>
        {moduleDevices.length > 0 && (
          <Badge color={defectCount > 0 ? "red" : "gray"} variant="light">
            {t("shipmentDetail.defectStat", {
              n: defectCount,
              total: moduleDevices.length,
              pct: ((defectCount / moduleDevices.length) * 100).toFixed(1),
            })}
          </Badge>
        )}
      </Group>

      {showScanActions && (
        <Alert
          color="blue"
          variant="light"
          title={t("shipmentDetail.scanActionsTitle")}
          withCloseButton
          onClose={() => setShowScanActions(false)}
          mb="lg"
        >
          <Group gap="xs" wrap="wrap">
            <Button
              size="xs"
              leftSection={<IconPrinter size={14} />}
              onClick={() => { setShowScanActions(false); void handlePrintPartida("small"); }}
            >
              {t("shipmentDetail.print53x30")}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconClipboardText size={14} />}
              onClick={() => { setShowScanActions(false); setActOpen(true); }}
            >
              {t("shipmentDetail.exportActBtn")}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPrinter size={14} />}
              onClick={() => { setShowScanActions(false); setBatchOpen(true); }}
            >
              {t("shipmentDetail.qrGroupsMenu")}
            </Button>
          </Group>
        </Alert>
      )}

      <Paper withBorder p="md" mb="lg">
        <Group justify="flex-start" wrap="wrap" gap="lg" align="flex-start">
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {t("shipments.groupExport")}
            </Text>
            <Group gap="xs" wrap="wrap">
            <Menu shadow="md" withinPortal>
              <Menu.Target>
                <Button size="sm" leftSection={<IconDownload size={16} />}>
                  {t("shipmentDetail.exportMenu")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  disabled={devices.length === 0}
                  onClick={() => handleExport(false)}
                >
                  {t("shipmentDetail.exportXlsx")}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileSpreadsheet size={14} />}
                  disabled={devices.length === 0}
                  onClick={() => handleExport(true)}
                >
                  {t("shipmentDetail.exportXlsxAccounting")}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconQrcode size={14} />}
                  disabled={devices.length === 0}
                  onClick={() => setBatchOpen(true)}
                >
                  {t("shipmentDetail.exportQrGroups")}
                </Menu.Item>
                <Menu.Item leftSection={<IconTable size={14} />} disabled={devices.length === 0} onClick={() => setSheetOpen(true)}>
                  {t("shipments.exportSheet")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            </Group>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {t("shipments.groupConfirm")}
            </Text>
            <Group gap="xs" wrap="wrap" align="flex-end">
            {shipment?.drive_export_url && (
              <Anchor href={shipment.drive_export_url} target="_blank" size="sm">
                {t("shipmentDetail.viewOnDrive")}
              </Anchor>
            )}
            {shipment && (shipment.status === "shipped" || shipment.status === "delivered") &&
              shipment.chirpstack_added_at === null && (
                <Button
                  color="teal"
                  size="sm"
                  leftSection={<IconCheck size={16} />}
                  onClick={async () => {
                    const res = await api.markChirpstackDone(shipment.id);
                    setShipment(res.shipment);
                    notifications.show({ color: "green", message: t("shipments.chirpstackMarked") });
                  }}
                >
                  {t("shipments.markChirpstackDone")}
                </Button>
              )}
            {shipment && shipment.chirpstack_added_at !== null && (
              <Group gap="xs">
                <Badge color="gray" variant="light">
                  {t("shipments.chirpstackBadge", { ts: new Date(shipment.chirpstack_added_at).toLocaleString() })}
                </Badge>
                {user.role === "admin" && (
                  <Anchor
                    component="button"
                    type="button"
                    size="xs"
                    onClick={async () => {
                      const res = await api.undoChirpstackDone(shipment.id);
                      setShipment(res.shipment);
                    }}
                  >
                    {t("shipments.chirpstackUndo")}
                  </Anchor>
                )}
              </Group>
            )}
            {canEdit && shipment && shipment.accounting_confirmed_at === null && (
              <Button
                color="green"
                size="sm"
                leftSection={<IconCheck size={16} />}
                onClick={async () => {
                  const res = await api.confirmAccounting(shipment.id);
                  setShipment(res.shipment);
                  notifications.show({ color: "green", message: t("shipments.accountingMarked") });
                }}
              >
                {t("shipments.accountingConfirm")}
              </Button>
            )}
            {shipment && shipment.accounting_confirmed_at !== null && (
              <Group gap="xs">
                <Badge color="green" variant="light">
                  {t("shipments.accountingBadge", { ts: new Date(shipment.accounting_confirmed_at).toLocaleString() })}
                </Badge>
                {user.role === "admin" && (
                  <Anchor
                    component="button"
                    type="button"
                    size="xs"
                    onClick={async () => {
                      const res = await api.unconfirmAccounting(shipment.id);
                      setShipment(res.shipment);
                    }}
                  >
                    {t("shipments.accountingUndo")}
                  </Anchor>
                )}
              </Group>
            )}
            {canEdit && shipment && (
              <Button
                size="sm"
                color="red"
                variant={shipment.on_debt ? "filled" : "default"}
                onClick={async () => {
                  await api.markShipmentsDebt([shipment.id], !shipment.on_debt);
                  setShipment({ ...shipment, on_debt: !shipment.on_debt });
                  notifications.show({ color: "green", message: t("shipments.debtToggled") });
                }}
              >
                {shipment.on_debt ? t("shipments.unmarkDebt") : t("shipments.markDebtBtn")}
              </Button>
            )}
            </Group>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {t("shipments.groupManage")}
            </Text>
            <Group gap="xs" wrap="wrap" align="flex-end">
            {canEdit && (
              <Button
                size="sm"
                variant="default"
                leftSection={<IconEdit size={16} />}
                onClick={() => setEditOpen(true)}
              >
                {t("shipmentDetail.editShipment")}
              </Button>
            )}
            {canEdit && (
              <Menu shadow="md" position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    size="sm"
                    variant="default"
                    leftSection={<IconDotsVertical size={16} />}
                  >
                    {t("common.more")}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <Menu.Label>{t("shipmentDetail.menuDevices")}</Menu.Label>
                  <Menu.Item
                    leftSection={<IconFileImport size={14} />}
                    onClick={() => setImportOpen(true)}
                  >
                    {t("shipmentDetail.addFromFile")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => setBulkOpen(true)}
                    disabled={devices.length === 0}
                  >
                    {t("shipmentDetail.assignBoxes")}
                  </Menu.Item>
                  {canEdit && (
                    <Menu.Item
                      leftSection={<IconBrandGoogleDrive size={14} />}
                      onClick={handleUploadDrive}
                      disabled={devices.length === 0}
                    >
                      {t("shipmentDetail.uploadDrive")}
                    </Menu.Item>
                  )}
                  {canEdit && (
                    <Menu.Item
                      leftSection={<IconBox size={14} />}
                      onClick={() => setDistributeOpen(true)}
                      disabled={devices.length === 0}
                    >
                      {t("shipmentDetail.distributeBoxes")}
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  <Menu.Label>{t("shipmentDetail.menuPartidaLabels")}</Menu.Label>
                  <Menu.Item
                    leftSection={<IconPrinter size={14} />}
                    onClick={() => void handlePrintPartida("small")}
                  >
                    {t("shipmentDetail.print53x30")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconPrinter size={14} />}
                    onClick={() => void handlePrintPartida("large")}
                  >
                    {t("shipmentDetail.print54x40")}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconLink size={14} />} onClick={handlePrintLink}>
                    {t("shipmentDetail.printLinkLabel")}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconCopy size={14} />} onClick={handleCopyPublicLink}>
                    {t("shipmentDetail.copyPublicLink")}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={handleRevokePublicLink}>
                    {t("shipmentDetail.revokePublicLink")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconPrinter size={14} />}
                    onClick={() => setBatchOpen(true)}
                  >
                    {t("shipmentDetail.qrGroupsMenu")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconPrinter size={14} />}
                    onClick={() => setPassportOpen(true)}
                  >
                    {t("station.btnPassport")}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Label>{t("shipmentDetail.menuDebug")}</Menu.Label>
                  <Menu.Item
                    leftSection={<IconCode size={14} />}
                    onClick={() => void handlePreviewEzpl("small")}
                  >
                    {t("shipmentDetail.previewEzpl53")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCode size={14} />}
                    onClick={() => void handlePreviewEzpl("large")}
                  >
                    {t("shipmentDetail.previewEzpl54")}
                  </Menu.Item>
                  {canDelete && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={handleDelete}
                      >
                        {t("shipmentDetail.deleteShipment")}
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            </Group>
          </Stack>
        </Group>
      </Paper>

      <Group mb="sm" wrap="wrap" gap="sm">
        <TextInput
          placeholder={t("shipmentDetail.searchPlaceholder")}
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ flex: "1 1 240px", maxWidth: 360 }}
        />
        <Text size="sm" c="dimmed">
          {t("shipmentDetail.devicesCounter", {
            filtered: filtered.length,
            total: devices.length,
          })}
        </Text>
      </Group>

      {canEdit && selected.size > 0 && (
        <Paper withBorder p="xs" mb="xs">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" fw={500}>
              {t("shipmentDetail.selected", { count: selected.size })}
            </Text>
            <Group gap="xs" wrap="wrap">
              <Button size="xs" onClick={() => setSetBoxOpen(true)}>
                {t("shipmentDetail.setBox")}
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconArrowsRight size={12} />}
                onClick={() => setMoveShipmentOpen(true)}
              >
                {t("shipmentDetail.moveToShipment")}
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={() =>
                  applyBulk(
                    { shipment_id: null, box_number: null },
                    t("shipmentDetail.removedFromShipmentToast"),
                  )
                }
              >
                {t("shipmentDetail.removeFromShipment")}
              </Button>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconEdit size={12} />}
                onClick={() => setBulkEditDevicesOpen(true)}
              >
                {t("shipmentDetail.editN", { count: selected.size })}
              </Button>
              {canEdit && (
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => setMarkDefectOpen(true)}
                >
                  {t("shipmentDetail.markDefectBulk", { count: selected.size })}
                </Button>
              )}
              {user.role === "admin" && (
                <Button
                  size="xs"
                  color="red"
                  variant="subtle"
                  leftSection={<IconTrash size={12} />}
                  onClick={deleteSelected}
                >
                  {t("common.delete")}
                </Button>
              )}
              <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>
                {t("common.cancel")}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <Paper withBorder p="md" mb="md">
        <Title order={5} mb="sm">{t("shipments.docs.title")}</Title>
        {/* re-mount on status change so files added via the status gate show up */}
        <ShipmentDocuments key={`${shipmentId}-${shipment?.status}`} shipmentId={shipmentId} canEdit={canEdit} />
      </Paper>

      <ResponsiveTable
        stickyColumns={canEdit ? 2 : 1}
        mobile={{
          rows: pageDevices,
          rowKey: (d) => d.id,
          title: (d) => <Text ff="monospace" size="sm" span>{d.dev_eui.toLowerCase()}</Text>,
          subtitle: (d) => d.model ?? t("common.none"),
          badge: (d) => <Badge size="sm" variant="light">{d.status}</Badge>,
          fields: [
            {
              label: "APP_KEY",
              render: (d) => (
                <Text ff="monospace" size="sm" style={{ wordBreak: "break-all" }}>
                  {d.app_key?.toLowerCase() ?? t("common.none")}
                </Text>
              ),
            },
            {
              label: "NR.CUTIE",
              render: (d) => canEdit ? (
                <BoxInput initial={d.box_number ?? ""} onSave={(value) => updateBox(d.id, value)} />
              ) : (
                <Text>{d.box_number ?? t("common.none")}</Text>
              ),
            },
            {
              label: t("shipmentDetail.colScanOrder"),
              render: (d) => scanOrder.has(d.id) ? (
                <Badge size="sm" variant="light" color="cyan">#{scanOrder.get(d.id)}</Badge>
              ) : (
                <Text size="xs" c="dimmed">{t("common.none")}</Text>
              ),
            },
          ],
          actions: canEdit
            ? (d) => (
                <>
                  <ActionIcon variant="light" size="lg" title={t("common.edit")} onClick={() => setEditingDevice(d)}>
                    <IconEdit size={18} />
                  </ActionIcon>
                  <ActionIcon variant="light" size="lg" title={t("shipmentDetail.printLabel")} onClick={() => printDeviceLabel(d.id)}>
                    <IconPrinter size={18} />
                  </ActionIcon>
                </>
              )
            : undefined,
          selection: canEdit
            ? {
                isSelected: (d) => selected.has(d.id),
                toggle: (d) => toggleSelected(d.id),
                allSelected: pageDevices.length > 0 && pageDevices.every((d) => selected.has(d.id)),
                someSelected: pageDevices.some((d) => selected.has(d.id)),
                toggleAll: () => toggleSelectAllOnPage(pageDevices),
              }
            : undefined,
          loading: loading && devices.length === 0,
          empty: t("shipmentDetail.noResults"),
        }}
      >
      <Table.ScrollContainer minWidth={780}>
      <Table striped withTableBorder highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {canEdit && (
              <Table.Th w={36}>
                <Checkbox
                  checked={
                    pageDevices.length > 0 && pageDevices.every((d) => selected.has(d.id))
                  }
                  indeterminate={
                    pageDevices.some((d) => selected.has(d.id)) &&
                    !pageDevices.every((d) => selected.has(d.id))
                  }
                  onChange={() => toggleSelectAllOnPage(pageDevices)}
                  aria-label={t("shipmentDetail.selectAllOnPage")}
                />
              </Table.Th>
            )}
            <Table.Th>DEV_EUI</Table.Th>
            <Table.Th>APP_KEY</Table.Th>
            <Table.Th>{t("shipmentDetail.colModel")}</Table.Th>
            <Table.Th>NR.CUTIE</Table.Th>
            <Table.Th w={90} style={{ whiteSpace: "nowrap" }}>
              {t("shipmentDetail.colScanOrder")}
            </Table.Th>
            <Table.Th style={{ whiteSpace: "nowrap" }}>{t("shipmentDetail.colStatus")}</Table.Th>
            <Table.Th w={50} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 7}>
                <Text c="dimmed">{t("common.loading")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && filtered.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 7}>
                <Text c="dimmed">{t("shipmentDetail.noResults")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {pageDevices.map((d) => (
            <Table.Tr key={d.id}>
              {canEdit && (
                <Table.Td>
                  <Checkbox
                    checked={selected.has(d.id)}
                    onChange={() => toggleSelected(d.id)}
                    aria-label={t("shipmentDetail.selectOne", { eui: d.dev_eui })}
                  />
                </Table.Td>
              )}
              <Table.Td>
                <Text ff="monospace">{d.dev_eui.toLowerCase()}</Text>
              </Table.Td>
              <Table.Td>
                <Text ff="monospace" size="sm" truncate maw={240}>
                  {d.app_key?.toLowerCase() ?? t("common.none")}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{d.model ?? t("common.none")}</Text>
              </Table.Td>
              <Table.Td style={{ minWidth: 140 }}>
                {canEdit ? (
                  <BoxInput
                    initial={d.box_number ?? ""}
                    onSave={(value) => updateBox(d.id, value)}
                  />
                ) : (
                  <Text>{d.box_number ?? t("common.none")}</Text>
                )}
              </Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                {scanOrder.has(d.id) ? (
                  <Tooltip
                    label={d.scanned_at ? new Date(d.scanned_at).toLocaleString() : ""}
                    disabled={!d.scanned_at}
                  >
                    <Badge size="sm" variant="light" color="cyan">
                      #{scanOrder.get(d.id)}
                    </Badge>
                  </Tooltip>
                ) : (
                  <Text size="xs" c="dimmed">
                    {t("common.none")}
                  </Text>
                )}
              </Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                <Badge size="sm" variant="light">
                  {d.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  {canEdit && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      title={t("common.edit")}
                      onClick={() => setEditingDevice(d)}
                    >
                      <IconEdit size={14} />
                    </ActionIcon>
                  )}
                  {canEdit && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      title={t("shipmentDetail.printLabel")}
                      onClick={() => printDeviceLabel(d.id)}
                    >
                      <IconPrinter size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      <BulkBoxModal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        shipmentId={shipmentId}
        devices={devices}
        onSaved={() => {
          setBulkOpen(false);
          load();
        }}
      />

      {shipment && (
        <EditShipmentDrawer
          opened={editOpen}
          onClose={() => setEditOpen(false)}
          shipment={shipment}
          onSaved={(s) => {
            setShipment(s);
            setEditOpen(false);
          }}
        />
      )}

      <Modal
        opened={batchOpen}
        onClose={() => setBatchOpen(false)}
        title={t("shipmentDetail.batchModalTitle")}
        size="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t("shipmentDetail.batchModalIntro")}
          </Text>
          <Text
            size="xs"
            c="dimmed"
            dangerouslySetInnerHTML={{ __html: t("shipmentDetail.batchModalXlsxHint") }}
          />
          <NumberInput
            label={t("shipmentDetail.batchChunkLabel")}
            description={t("shipmentDetail.batchChunkDesc", {
              total: devices.length,
              labels: Math.max(
                1,
                Math.ceil(devices.length / Math.max(1, batchChunkSize)),
              ),
            })}
            value={batchChunkSize}
            onChange={(v) => setBatchChunkSize(typeof v === "number" ? v : 25)}
            min={1}
            max={100}
            data-autofocus
          />
          <Switch
            label={t("shipmentDetail.useModelLabel")}
            description={t("shipmentDetail.useModelDesc")}
            checked={batchUseModel}
            onChange={(e) => setBatchUseModel(e.currentTarget.checked)}
          />
          <Group justify="space-between" wrap="wrap">
            <Button
              variant="light"
              leftSection={<IconDownload size={14} />}
              onClick={() => {
                window.location.href = api.exportShipmentQrGroupsUrl(shipmentId, {
                  chunk_size: Math.max(1, Math.min(100, batchChunkSize || 25)),
                  use_model: batchUseModel,
                });
              }}
              disabled={devices.length === 0}
            >
              {t("shipmentDetail.downloadXlsx")}
            </Button>
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setBatchOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => void handlePrintBatches()}
                loading={batchSubmitting}
                leftSection={<IconPrinter size={14} />}
              >
                {t("shipmentDetail.printNLabels", {
                  count: Math.max(
                    1,
                    Math.ceil(devices.length / Math.max(1, batchChunkSize)),
                  ),
                })}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={ezplPreview !== null}
        onClose={() => setEzplPreview(null)}
        title={
          ezplPreview
            ? t("shipmentDetail.ezplModalTitle", {
                code: ezplPreview.code,
                size: ezplPreview.size === "large" ? "54×40" : "53×30",
                bytes: ezplPreview.bytes,
              })
            : "EZPL"
        }
        size="xl"
      >
        {ezplPreview && (
          <Stack>
            <Group justify="flex-end" gap="xs" wrap="wrap">
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  void navigator.clipboard.writeText(ezplPreview.ezpl);
                  notifications.show({
                    color: "green",
                    message: t("shipmentDetail.copiedToClipboard"),
                  });
                }}
              >
                {t("shipmentDetail.copy")}
              </Button>
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  const blob = new Blob([ezplPreview.ezpl], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${ezplPreview.code}_${ezplPreview.size}.prn`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                {t("shipmentDetail.downloadPrn")}
              </Button>
              {user.role === "admin" && (
                <Button
                  size="xs"
                  variant="filled"
                  color="orange"
                  onClick={async () => {
                    try {
                      await api.godexPrintRaw(ezplPreview.ezpl);
                      notifications.show({
                        color: "green",
                        message: t("shipmentDetail.ezplSentRaw"),
                      });
                    } catch (err) {
                      const detail =
                        err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
                      notifications.show({
                        color: "red",
                        title: t("shipmentDetail.printRawFailed"),
                        message: detail.slice(0, 200),
                      });
                    }
                  }}
                >
                  {t("shipmentDetail.sendViaPrintRaw")}
                </Button>
              )}
            </Group>
            <Textarea
              value={ezplPreview.ezpl}
              readOnly
              autosize
              minRows={10}
              maxRows={30}
              styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
            />
            <Text size="xs" c="dimmed">
              {t("shipmentDetail.ezplHint")}
            </Text>
          </Stack>
        )}
      </Modal>

      <SetBoxModal
        opened={setBoxOpen}
        onClose={() => setSetBoxOpen(false)}
        count={selected.size}
        onConfirm={async (box) => {
          await applyBulk({ box_number: box }, t("shipmentDetail.boxSetToast"));
          setSetBoxOpen(false);
        }}
      />

      <MoveTargetModal
        opened={moveShipmentOpen}
        onClose={() => setMoveShipmentOpen(false)}
        title={t("shipmentDetail.moveToShipmentTitle")}
        label={t("shipmentDetail.moveTargetLabel")}
        options={allShipments
          .filter((s) => s.id !== shipmentId)
          .map((s) => ({ value: String(s.id), label: `${s.code} · ${shipmentStatusLabel(t, s.status)}` }))}
        onConfirm={async (value) => {
          await applyBulk(
            { shipment_id: Number(value), box_number: null },
            t("shipmentDetail.movedToShipmentToast"),
          );
          setMoveShipmentOpen(false);
        }}
      />

      <BulkEditDevicesModal
        opened={bulkEditDevicesOpen}
        onClose={() => setBulkEditDevicesOpen(false)}
        count={selected.size}
        onConfirm={async (updates) => {
          await applyBulk(updates, t("shipmentDetail.editedToast"));
          setBulkEditDevicesOpen(false);
        }}
      />

      <ShipDeviceEditDrawer
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={(d) => {
          setDevices((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x)));
          setEditingDevice(null);
        }}
      />

      <ImportDevicesModal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        shipmentId={shipmentId}
        onImported={() => {
          setImportOpen(false);
          load();
        }}
      />

      <DistributeBoxesModal
        opened={distributeOpen}
        onClose={() => setDistributeOpen(false)}
        shipmentId={shipmentId}
        unboxedCount={devices.filter((d) => !d.box_number).length}
        totalCount={devices.length}
        onApplied={() => {
          setDistributeOpen(false);
          load();
        }}
      />

      <Modal
        opened={markDefectOpen}
        onClose={() => setMarkDefectOpen(false)}
        title={t("shipmentDetail.markDefectTitle")}
        centered
      >
        <Stack>
          <Text size="sm">{t("shipmentDetail.markDefectMsg", { count: selected.size })}</Text>
          <TextInput
            label={t("shipmentDetail.markDefectReason")}
            value={defectReason}
            onChange={(e) => setDefectReason(e.currentTarget.value)}
            placeholder={t("shipmentDetail.markDefectPlaceholder")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setMarkDefectOpen(false)}>{t("common.cancel")}</Button>
            <Button color="red" onClick={async () => {
              try {
                await api.bulkUpdateDevices(Array.from(selected), { status: "defect", notes_append: defectReason || "marked defect" });
                setMarkDefectOpen(false);
                setDefectReason("");
                setSelected(new Set());
                await load();
                notifications.show({ color: "teal", message: t("shipmentDetail.markDefectOk") });
              } catch (e) {
                notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
              }
            }}>{t("shipmentDetail.markDefect")}</Button>
          </Group>
        </Stack>
      </Modal>

      <ExportActModal
        opened={actOpen}
        onClose={() => setActOpen(false)}
        shipmentIds={[shipmentId]}
      />
      <ExportToSheetModal opened={sheetOpen} onClose={() => setSheetOpen(false)} target="shipments" ids={[shipmentId]} />
      <PrintPassportLabelsModal opened={passportOpen} onClose={() => setPassportOpen(false)} shipmentId={shipmentId} />
    </Container>
  );
}

interface DistributeProps {
  opened: boolean;
  onClose: () => void;
  shipmentId: number;
  unboxedCount: number;
  totalCount: number;
  onApplied: () => void;
}

interface RuleRow {
  id: number;
  count: string;
  box: string;
}

function DistributeBoxesModal({
  opened,
  onClose,
  shipmentId,
  unboxedCount,
  totalCount,
  onApplied,
}: DistributeProps) {
  const { t } = useTranslation();
  const [rules, setRules] = useState<RuleRow[]>([{ id: 1, count: "", box: "" }]);
  const [onlyUnboxed, setOnlyUnboxed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<
    | {
        applied: number;
        by_box: Record<string, number>;
        leftover_rule_capacity: number;
        leftover_devices: number;
      }
    | null
  >(null);

  useEffect(() => {
    if (opened) {
      setRules([{ id: Date.now(), count: "", box: "" }]);
      setOnlyUnboxed(true);
      setLastResult(null);
    }
  }, [opened]);

  function addRule() {
    setRules((prev) => [...prev, { id: Date.now() + prev.length, count: "", box: "" }]);
  }

  function removeRule(id: number) {
    setRules((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
  }

  function updateRule(id: number, patch: Partial<RuleRow>) {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  const totalRequested = rules.reduce(
    (a, r) => a + (parseInt(r.count, 10) || 0),
    0,
  );
  const eligible = onlyUnboxed ? unboxedCount : totalCount;

  async function apply() {
    const payload = rules
      .map((r) => ({ count: parseInt(r.count, 10) || 0, box_number: r.box.trim() }))
      .filter((r) => r.count > 0 && r.box_number);
    if (payload.length === 0) {
      notifications.show({
        color: "yellow",
        message: t("shipmentDetail.distRulesRequired"),
      });
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.distributeBoxes(shipmentId, payload, onlyUnboxed);
      setLastResult(r);
      notifications.show({
        color: r.applied > 0 ? "green" : "yellow",
        message: t("shipmentDetail.distributedToast", {
          applied: r.applied,
          boxes: Object.keys(r.by_box).length,
        }),
      });
      onApplied();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.distributeFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipmentDetail.distributeTitle")} size="lg">
      <Stack>
        <Alert
          variant="light"
          color="blue"
          dangerouslySetInnerHTML={{
            __html: t("shipmentDetail.distributeIntro", {
              count: rules[0]?.count || "?",
              box: rules[0]?.box || "?",
            }),
          }}
        />

        <Select
          label={t("shipmentDetail.applyToLabel")}
          data={[
            {
              value: "unboxed",
              label: t("shipmentDetail.onlyUnboxedOption", { count: unboxedCount }),
            },
            {
              value: "all",
              label: t("shipmentDetail.allOption", { count: totalCount }),
            },
          ]}
          value={onlyUnboxed ? "unboxed" : "all"}
          onChange={(v) => setOnlyUnboxed(v === "unboxed")}
          allowDeselect={false}
        />

        <Divider label={t("shipmentDetail.rulesDivider")} labelPosition="center" />

        <Stack gap="xs">
          {rules.map((r, idx) => (
            <Group key={r.id} gap="xs" wrap="nowrap" align="flex-end">
              <TextInput
                label={idx === 0 ? t("shipmentDetail.ruleCountLabel") : undefined}
                type="number"
                placeholder="50"
                value={r.count}
                onChange={(e) => updateRule(r.id, { count: e.currentTarget.value })}
                w={120}
              />
              <Text c="dimmed" mb={6}>
                →
              </Text>
              <TextInput
                label={idx === 0 ? t("shipmentDetail.ruleBoxLabel") : undefined}
                placeholder="2700"
                value={r.box}
                onChange={(e) => updateRule(r.id, { box: e.currentTarget.value })}
                style={{ flex: 1 }}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => removeRule(r.id)}
                disabled={rules.length === 1}
                mb={6}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={12} />}
            onClick={addRule}
            style={{ alignSelf: "flex-start" }}
          >
            {t("shipmentDetail.addBoxRule")}
          </Button>
        </Stack>

        <Group justify="space-between">
          <Text
            size="xs"
            c={totalRequested > eligible ? "yellow" : "dimmed"}
            dangerouslySetInnerHTML={{
              __html:
                t("shipmentDetail.totalRequested", {
                  requested: totalRequested,
                  eligible,
                }) +
                (totalRequested > eligible
                  ? t("shipmentDetail.surplusRules")
                  : "") +
                (totalRequested < eligible
                  ? t("shipmentDetail.leftoverDevices", {
                      count: eligible - totalRequested,
                    })
                  : ""),
            }}
          />
        </Group>

        {lastResult && (
          <Alert
            color={lastResult.applied > 0 ? "green" : "yellow"}
            title={t("shipmentDetail.distributeResult")}
          >
            <Stack gap={4}>
              <Text
                size="sm"
                dangerouslySetInnerHTML={{
                  __html:
                    t("shipmentDetail.appliedLine", { count: lastResult.applied }) +
                    (lastResult.leftover_devices > 0
                      ? t("shipmentDetail.leftoverDevicesLine", {
                          count: lastResult.leftover_devices,
                        })
                      : "") +
                    (lastResult.leftover_rule_capacity > 0
                      ? t("shipmentDetail.leftoverCapacityLine", {
                          count: lastResult.leftover_rule_capacity,
                        })
                      : ""),
                }}
              />
              {Object.keys(lastResult.by_box).length > 0 && (
                <Text size="xs" c="dimmed">
                  {Object.entries(lastResult.by_box)
                    .map(([b, n]) => `${b}: ${n}`)
                    .join(" · ")}
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.close")}
          </Button>
          <Button onClick={apply} loading={submitting}>
            {t("common.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface ImportProps {
  opened: boolean;
  onClose: () => void;
  shipmentId: number;
  onImported: () => void;
}

function ImportDevicesModal({ opened, onClose, shipmentId, onImported }: ImportProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [boxNumber, setBoxNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<
    | (BulkAssignResult & { queries_total: number })
    | null
  >(null);
  const [antennaCands, setAntennaCands] = useState<string[]>([]);
  const [antennaOpen, setAntennaOpen] = useState(false);
  const [antennaCreating, setAntennaCreating] = useState(false);
  const [lastBox, setLastBox] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setFile(null);
      setPasteText("");
      setBoxNumber("");
      setResult(null);
      setAntennaOpen(false);
    }
  }, [opened]);

  function offerAntennas(r: BulkAssignResult) {
    const cands = antennaCandidates(r.not_found);
    setLastBox(r.box_number ?? null);
    if (cands.length > 0) {
      setAntennaCands(cands);
      setAntennaOpen(true);
    }
  }

  async function confirmAntennas(selected: string[]) {
    if (selected.length === 0) return;
    setAntennaCreating(true);
    try {
      const r = await api.createAntennas(shipmentId, selected, lastBox || null);
      const doneKeys = new Set([...r.applied, ...r.conflicts].map((x) => cleanDevEui(x.dev_eui)));
      setResult((prev) =>
        prev
          ? {
              ...prev,
              applied: [...prev.applied, ...r.applied],
              conflicts: [...prev.conflicts, ...r.conflicts],
              not_found: prev.not_found.filter((q) => !doneKeys.has(cleanDevEui(q))),
            }
          : prev,
      );
      notifications.show({
        color: r.applied.length > 0 ? "green" : "yellow",
        message: t("antennaConfirm.done", { count: r.applied.length }),
      });
      const skipped = antennaSkippedMessage(t, r);
      if (skipped) notifications.show({ color: "yellow", message: skipped });
      setAntennaOpen(false);
      if (r.applied.length > 0) onImported();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.importFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setAntennaCreating(false);
    }
  }

  async function submitFile() {
    if (!file) return;
    setSubmitting(true);
    try {
      const r = await api.importShipmentFile(shipmentId, file, boxNumber || undefined);
      setResult(r);
      notifications.show({
        color: r.applied.length > 0 ? "green" : "yellow",
        message: t("shipmentDetail.importToast", {
          applied: r.applied.length,
          total: r.queries_total,
          notFound: r.not_found.length,
          conflicts: r.conflicts.length,
        }),
      });
      if (r.applied.length > 0) onImported();
      offerAntennas(r);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.importFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function submitText() {
    if (!pasteText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.importShipmentText(shipmentId, pasteText, boxNumber || undefined);
      setResult(r);
      notifications.show({
        color: r.applied.length > 0 ? "green" : "yellow",
        message: t("shipmentDetail.importToast", {
          applied: r.applied.length,
          total: r.queries_total,
          notFound: r.not_found.length,
          conflicts: r.conflicts.length,
        }),
      });
      if (r.applied.length > 0) onImported();
      offerAntennas(r);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.importFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("shipmentDetail.importTitle")}
      size="lg"
    >
      <Tabs defaultValue="file">
        <Tabs.List>
          <Tabs.Tab value="file" leftSection={<IconUpload size={14} />}>
            {t("shipmentDetail.tabFile")}
          </Tabs.Tab>
          <Tabs.Tab value="paste" leftSection={<IconClipboardText size={14} />}>
            {t("shipmentDetail.tabPaste")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="file" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("shipmentDetail.importFileHint")}
            </Text>
            <FileInput
              label={t("shipmentDetail.fileLabel")}
              placeholder={t("shipmentDetail.filePlaceholder")}
              accept=".xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              value={file}
              onChange={setFile}
            />
            <TextInput
              label={t("shipmentDetail.boxOptionalLabel")}
              placeholder={t("shipmentDetail.boxOptionalPlaceholder")}
              value={boxNumber}
              onChange={(e) => setBoxNumber(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button onClick={submitFile} disabled={!file} loading={submitting}>
                {t("shipmentDetail.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="paste" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("shipmentDetail.pasteImportHint")}
            </Text>
            <Textarea
              autosize
              minRows={6}
              maxRows={14}
              placeholder={
                "aabbccddeeff0011\n3020.50000002.2511\n..."
              }
              value={pasteText}
              onChange={(e) => setPasteText(e.currentTarget.value)}
            />
            <TextInput
              label={t("shipmentDetail.boxOptionalLabel")}
              placeholder={t("shipmentDetail.boxOptionalPlaceholder")}
              value={boxNumber}
              onChange={(e) => setBoxNumber(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button onClick={submitText} disabled={!pasteText.trim()} loading={submitting}>
                {t("shipmentDetail.import")}
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {result && (
        <Alert
          mt="md"
          color={result.applied.length > 0 ? "green" : "yellow"}
          title={t("shipmentDetail.importResult")}
        >
          <Stack gap={4}>
            <Text
              size="sm"
              dangerouslySetInnerHTML={{
                __html: t("shipmentDetail.importStats", {
                  applied: result.applied.length,
                  conflicts: result.conflicts.length,
                  notFound: result.not_found.length,
                  total: result.queries_total,
                }),
              }}
            />
            {result.conflicts.length > 0 && (
              <Text size="xs" c="dimmed">
                {t("shipmentDetail.conflictsLabel", {
                  list:
                    result.conflicts
                      .slice(0, 5)
                      .map((c) =>
                        t("shipmentDetail.conflictInShipment", {
                          eui: c.dev_eui,
                          code:
                            c.in_shipment_code ??
                            t("shipmentDetail.conflictUnknownShipment"),
                        }),
                      )
                      .join(", ") +
                    (result.conflicts.length > 5
                      ? ` (+${result.conflicts.length - 5})`
                      : ""),
                })}
              </Text>
            )}
            {result.not_found.length > 0 && (
              <Text size="xs" c="dimmed">
                {t("shipmentDetail.notFoundLabel", {
                  list:
                    result.not_found
                      .slice(0, 5)
                      .map((q) => q.slice(0, 32))
                      .join(", ") +
                    (result.not_found.length > 5
                      ? ` (+${result.not_found.length - 5})`
                      : ""),
                })}
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <AntennaConfirmModal
        opened={antennaOpen}
        candidates={antennaCands}
        loading={antennaCreating}
        onConfirm={(sel) => void confirmAntennas(sel)}
        onCancel={() => setAntennaOpen(false)}
      />
    </Modal>
  );
}

interface EditShipmentProps {
  opened: boolean;
  onClose: () => void;
  shipment: Shipment;
  onSaved: (s: Shipment) => void;
}

function EditShipmentDrawer({ opened, onClose, shipment, onSaved }: EditShipmentProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(shipment.code);
  const [customerId, setCustomerId] = useState<string | null>(
    shipment.customer_id ? String(shipment.customer_id) : null,
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [location, setLocation] = useState(shipment.location ?? "");
  const [branch, setBranch] = useState(shipment.branch ?? "");
  const [shippedDate, setShippedDate] = useState(shipment.shipped_date ?? "");
  const [status, setStatus] = useState<ShipmentStatus>(shipment.status);
  const [notes, setNotes] = useState(shipment.notes ?? "");
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryType[]>([]);
  const [deliveryTypeId, setDeliveryTypeId] = useState<string | null>(
    shipment.delivery_type_id ? String(shipment.delivery_type_id) : null,
  );
  const [submitting, setSubmitting] = useState(false);
  const docsGate = useStatusDocsGate();

  useEffect(() => {
    setCode(shipment.code);
    setCustomerId(shipment.customer_id ? String(shipment.customer_id) : null);
    setLocation(shipment.location ?? "");
    setBranch(shipment.branch ?? "");
    setShippedDate(shipment.shipped_date ?? "");
    setStatus(shipment.status);
    setNotes(shipment.notes ?? "");
    setDeliveryTypeId(shipment.delivery_type_id ? String(shipment.delivery_type_id) : null);
  }, [shipment]);

  useEffect(() => {
    if (opened) {
      void api.listCustomers().then(setCustomers).catch(() => undefined);
      void api.listDeliveryTypes(true).then(setDeliveryTypes).catch(() => undefined);
    }
  }, [opened]);

  // Shipped/Delivered: ask first whether there's a document/photo to attach.
  // A shipment that was already "shipped" won't re-trigger the auto group send,
  // so its new files must be sent to accounting explicitly.
  function save() {
    if (status !== shipment.status) {
      docsGate.request([shipment.id], status, doSave, { shipAutoSends: shipment.status !== "shipped" });
    } else {
      void doSave();
    }
  }

  async function doSave(): Promise<boolean> {
    setSubmitting(true);
    try {
      const trimmedCode = code.trim();
      if (!trimmedCode) {
        notifications.show({ color: "red", message: t("shipmentDetail.codeRequired") });
        return false;
      }
      const updated = await api.updateShipment(shipment.id, {
        code: trimmedCode,
        customer_id: customerId ? Number(customerId) : null,
        location: location || null,
        branch: branch || null,
        shipped_date: shippedDate || null,
        status,
        notes: notes || null,
        delivery_type_id: deliveryTypeId ? Number(deliveryTypeId) : null,
      });
      notifications.show({ color: "green", message: t("shipmentDetail.shipmentUpdated") });
      onSaved(updated);
      return true;
    } catch (err) {
      const status_ = err instanceof ApiError ? err.status : 0;
      if (status_ === 409) {
        notifications.show({
          color: "red",
          title: t("shipmentDetail.codeExistsTitle"),
          message: t("shipmentDetail.codeExistsBody"),
        });
        return false;
      }
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.saveFailed"),
        message: JSON.stringify(detail).slice(0, 200),
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t("shipmentDetail.editShipmentTitle")}
      position="right"
      size="md"
    >
      <Stack>
        <TextInput
          label={t("shipmentDetail.codeLabel")}
          required
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />
        <Select
          label={t("shipmentDetail.customerLabel")}
          placeholder={t("shipmentDetail.customerPlaceholder")}
          description={t("shipmentDetail.customerDesc")}
          data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
          value={customerId}
          onChange={(v) => {
            setCustomerId(v);
            const cust = customers.find((c) => String(c.id) === v);
            if (cust?.city) setLocation(cust.city);
            if (cust?.branch) setBranch(cust.branch);
            setShippedDate(new Date().toISOString().slice(0, 10));
          }}
          searchable
          clearable
        />
        <Select
          label={t("shipments.deliveryTypeLabel")}
          data={deliveryTypes.map((dt) => ({ value: String(dt.id), label: deliveryTypeLabel(t, dt.slug, dt.name) }))}
          value={deliveryTypeId}
          onChange={setDeliveryTypeId}
          clearable
        />
        <Select
          label={t("shipmentDetail.statusLabel")}
          data={[
            { value: "draft", label: shipmentStatusLabel(t, "draft") },
            { value: "ready", label: shipmentStatusLabel(t, "ready") },
            { value: "shipped", label: shipmentStatusLabel(t, "shipped") },
            { value: "delivered", label: shipmentStatusLabel(t, "delivered") },
          ]}
          value={status}
          onChange={(v) => v && setStatus(v as ShipmentStatus)}
          allowDeselect={false}
        />
        <TextInput
          label={t("shipmentDetail.editLocationLabel")}
          value={location}
          onChange={(e) => setLocation(e.currentTarget.value)}
        />
        <TextInput
          label={t("shipmentDetail.editBranchLabel")}
          value={branch}
          onChange={(e) => setBranch(e.currentTarget.value)}
        />
        <TextInput
          label={t("shipmentDetail.shippedDateLabel")}
          placeholder="2026-05-08"
          value={shippedDate}
          onChange={(e) => setShippedDate(e.currentTarget.value)}
        />
        <Textarea
          label={t("shipmentDetail.notesLabel")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={3}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save} loading={submitting}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Drawer>
    {docsGate.element}
    </>
  );
}

function BoxInput({ initial, onSave }: { initial: string; onSave: (v: string) => Promise<void> | void }) {
  const [value, setValue] = useState(initial);
  const [editing, setEditing] = useState(false);

  useEffect(() => setValue(initial), [initial]);

  if (!editing) {
    return (
      <Group gap={4} wrap="nowrap">
        <Text size="sm" style={{ flex: 1 }}>
          {initial || <span style={{ color: "var(--mantine-color-dimmed)" }}>—</span>}
        </Text>
        <ActionIcon size="sm" variant="subtle" onClick={() => setEditing(true)}>
          <IconEdit size={12} />
        </ActionIcon>
      </Group>
    );
  }

  async function commit() {
    await onSave(value);
    setEditing(false);
  }

  return (
    <Group gap={4} wrap="nowrap">
      <TextInput
        size="xs"
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setValue(initial);
            setEditing(false);
          }
        }}
        autoFocus
      />
      <ActionIcon size="sm" color="green" variant="subtle" onClick={commit}>
        <IconCheck size={14} />
      </ActionIcon>
    </Group>
  );
}

interface BulkProps {
  opened: boolean;
  onClose: () => void;
  shipmentId: number;
  devices: Device[];
  onSaved: () => void;
}

function BulkBoxModal({ opened, onClose, shipmentId, devices, onSaved }: BulkProps) {
  const { t } = useTranslation();
  const [boxName, setBoxName] = useState("");
  const [devEuiList, setDevEuiList] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) {
      setBoxName("");
      setDevEuiList("");
    }
  }, [opened]);

  async function handleSubmit() {
    const targetEuis = devEuiList
      .split(/[\s,;]+/)
      .map((s) => s.trim().toUpperCase().replace(/[:\-]/g, ""))
      .filter((s) => s.length === 16);

    if (targetEuis.length === 0) {
      notifications.show({ color: "red", message: t("shipmentDetail.noValidDevEui") });
      return;
    }
    if (!boxName.trim()) {
      notifications.show({ color: "red", message: t("shipmentDetail.boxEmpty") });
      return;
    }

    const euiSet = new Set(targetEuis);
    const updates = devices
      .filter((d) => euiSet.has(d.dev_eui.toUpperCase()))
      .map((d) => ({ device_id: d.id, box_number: boxName.trim() }));

    if (updates.length === 0) {
      notifications.show({ color: "yellow", message: t("shipmentDetail.noMatchingDevices") });
      return;
    }

    setSubmitting(true);
    try {
      const r = await api.setShipmentBoxes(shipmentId, updates);
      notifications.show({
        color: "green",
        message: t("shipmentDetail.bulkBoxUpdated", { count: r.updated }),
      });
      onSaved();
    } catch {
      notifications.show({ color: "red", message: t("shipmentDetail.saveFailed") });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipmentDetail.bulkBoxTitle")} size="lg">
      <Stack>
        <Alert color="blue" variant="light">
          {t("shipmentDetail.bulkBoxIntro")}
        </Alert>
        <TextInput
          label={t("shipmentDetail.boxNumberLabel")}
          placeholder="2600s"
          required
          value={boxName}
          onChange={(e) => setBoxName(e.currentTarget.value)}
        />
        <TextInput
          label={t("shipmentDetail.devEuiListLabel")}
          placeholder={t("shipmentDetail.devEuiListPlaceholder")}
          value={devEuiList}
          onChange={(e) => setDevEuiList(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          {t("shipmentDetail.bulkBoxHint")}
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            {t("common.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface MoveTargetProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  label: string;
  options: { value: string; label: string }[];
  onConfirm: (value: string) => Promise<void>;
}

function MoveTargetModal({ opened, onClose, title, label, options, onConfirm }: MoveTargetProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) setValue(null);
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <Stack>
        <Select
          label={label}
          data={options}
          value={value}
          onChange={setValue}
          searchable
          placeholder={t("common.select")}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!value}
            loading={submitting}
            onClick={async () => {
              if (!value) return;
              setSubmitting(true);
              try {
                await onConfirm(value);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {t("common.confirm")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface SetBoxProps {
  opened: boolean;
  onClose: () => void;
  count: number;
  onConfirm: (box: string) => Promise<void>;
}

function SetBoxModal({ opened, onClose, count, onConfirm }: SetBoxProps) {
  const { t } = useTranslation();
  const [box, setBox] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) setBox("");
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipmentDetail.setBoxTitle", { count })}>
      <Stack>
        <TextInput
          label={t("shipmentDetail.setBoxNumberLabel")}
          placeholder="2700s"
          value={box}
          onChange={(e) => setBox(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && box.trim()) {
              setSubmitting(true);
              onConfirm(box.trim()).finally(() => setSubmitting(false));
            }
          }}
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!box.trim()}
            loading={submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                await onConfirm(box.trim());
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {t("common.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface BulkEditDevicesProps {
  opened: boolean;
  onClose: () => void;
  count: number;
  onConfirm: (updates: Record<string, unknown>) => Promise<void>;
}

function BulkEditDevicesModal({ opened, onClose, count, onConfirm }: BulkEditDevicesProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string | null>(null);
  const [model, setModel] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [notesAppend, setNotesAppend] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) {
      setStatus(null);
      setModel("");
      setFirmwareVersion("");
      setNotesAppend("");
    }
  }, [opened]);

  async function submit() {
    const updates: Record<string, unknown> = {};
    if (status) updates.status = status;
    if (model) updates.model = model;
    if (firmwareVersion) updates.firmware_version = firmwareVersion;
    if (notesAppend) updates.notes_append = notesAppend;
    if (Object.keys(updates).length === 0) {
      notifications.show({ color: "yellow", message: t("shipmentDetail.noFieldFilled") });
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(updates);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipmentDetail.bulkEditTitle", { count })}>
      <Stack>
        <Text size="xs" c="dimmed">
          {t("shipmentDetail.bulkEditHint")}
        </Text>
        <Select
          label={t("shipmentDetail.statusLabel")}
          data={ALL_STATUSES.map((s) => ({ value: s, label: s }))}
          value={status}
          onChange={setStatus}
          clearable
          placeholder={t("shipmentDetail.bulkStatusKeep")}
        />
        <TextInput
          label={t("shipmentDetail.bulkModelLabel")}
          value={model}
          onChange={(e) => setModel(e.currentTarget.value)}
        />
        <TextInput
          label={t("shipmentDetail.bulkFirmwareLabel")}
          value={firmwareVersion}
          onChange={(e) => setFirmwareVersion(e.currentTarget.value)}
        />
        <Textarea
          label={t("shipmentDetail.bulkNotesAppendLabel")}
          placeholder={t("shipmentDetail.bulkNotesAppendPlaceholder")}
          value={notesAppend}
          onChange={(e) => setNotesAppend(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={submitting}>
            {t("common.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface ShipDeviceEditProps {
  device: Device | null;
  onClose: () => void;
  onSaved: (d: Device) => void;
}

function ShipDeviceEditDrawer({ device, onClose, onSaved }: ShipDeviceEditProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [boxNumber, setBoxNumber] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [linkedEui, setLinkedEui] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!device) return;
    setStatus(device.status);
    setLinkedEui(device.linked_dev_eui ?? "");
    setBoxNumber(device.box_number ?? "");
    setModel(device.model ?? "");
    setSerialNumber(device.serial_number ?? "");
    setFirmwareVersion(device.firmware_version ?? "");
    setNotes("");
  }, [device]);

  async function save() {
    if (!device) return;
    setSubmitting(true);
    try {
      const r = await api.updateDevice(device.id, {
        status: status as Device["status"],
        box_number: boxNumber || null,
        model: model || null,
        serial_number: serialNumber || null,
        firmware_version: firmwareVersion || null,
        notes: notes || null,
        ...(device.product_type_slug === "antenna" ? { linked_dev_eui: linkedEui.trim() || null } : {}),
      } as Partial<Device>);
      notifications.show({ color: "green", message: t("shipmentDetail.deviceUpdated") });
      onSaved(r.device);
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      notifications.show({
        color: "red",
        title: t("shipmentDetail.saveFailed"),
        message: antennaErrorMessage(err, t) ?? JSON.stringify(detail).slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      opened={device !== null}
      onClose={onClose}
      title={
        device
          ? t("shipmentDetail.deviceDrawerTitle", { eui: device.dev_eui.toLowerCase() })
          : ""
      }
      position="right"
      size="md"
    >
      {device && (
        <Stack>
          <Select
            label={t("shipmentDetail.statusLabel")}
            data={ALL_STATUSES.map((s) => ({ value: s, label: s }))}
            value={status}
            onChange={(v) => setStatus(v ?? "")}
            allowDeselect={false}
          />
          <TextInput
            label={t("shipmentDetail.setBoxNumberLabel")}
            value={boxNumber}
            onChange={(e) => setBoxNumber(e.currentTarget.value)}
          />
          <TextInput
            label={t("shipmentDetail.bulkModelLabel")}
            value={model}
            onChange={(e) => setModel(e.currentTarget.value)}
          />
          <TextInput
            label={t("shipmentDetail.deviceSerialLabel")}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.currentTarget.value)}
          />
          <TextInput
            label={t("shipmentDetail.bulkFirmwareLabel")}
            value={firmwareVersion}
            onChange={(e) => setFirmwareVersion(e.currentTarget.value)}
          />
          {device.product_type_slug === "antenna" && (
            <TextInput
              label={t("antennas.linkedModule")}
              description={t("antennas.linkedModuleHint")}
              value={linkedEui}
              onChange={(e) => setLinkedEui(e.currentTarget.value)}
              ff="monospace"
            />
          )}
          <Textarea
            label={t("shipmentDetail.deviceNotesOverwriteLabel")}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={save} loading={submitting}>
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
